import React from 'react';
import { Columns, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/core';

interface ColumnNodeConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    inputColumns: any[];
}

export const ColumnNodeConfig: React.FC<ColumnNodeConfigProps> = ({ data, onChange, inputColumns }) => {
    // Initialize config if empty
    React.useEffect(() => {
        if ((!data.columns || data.columns.length === 0) && inputColumns.length > 0) {
            const initialConfig = inputColumns.map(col => ({
                name: col.name,
                newName: col.name,
                keep: true,
                type: col.type
            }));
            onChange('columns', initialConfig);
        }
    }, [inputColumns, data.columns, onChange]);

    const activeCols = data.columns || [];

    // Auto-Detect / Reset to inputs
    const handleAutoDetect = () => {
        // console.log("ColumnNodeConfig: handleAutoDetect called. InputColumns:", inputColumns);
        if (inputColumns.length > 0) {
            const initialConfig = inputColumns.map(col => ({
                name: col.name,
                newName: col.name,
                keep: true,
                type: col.type
            }));
            onChange('columns', initialConfig);
            notify.success(`Auto-detected ${inputColumns.length} columns.`);
        } else {
            // console.warn("ColumnNodeConfig: No input columns available for auto-detect.");
            notify.warning("No input columns available to detect.");
        }
    };

    const [rowId, setRowId] = React.useState(data.generateRowId || false);

    React.useEffect(() => {
        setRowId(data.generateRowId || false);
    }, [data.generateRowId]);

    const toggleRowId = (checked: boolean) => {
        console.log("ColumnNodeConfig: toggleRowId called. Checked:", checked);
        setRowId(checked); // Immediate UI update
        onChange('generateRowId', checked);
    };

    const toggleKeep = (index: number) => {
        const newCols = [...activeCols];
        newCols[index].keep = !newCols[index].keep;
        onChange('columns', newCols);
    };

    const updateRename = (index: number, val: string) => {
        const newCols = [...activeCols];
        newCols[index].newName = val;
        onChange('columns', newCols);
    };

    const moveCol = (index: number, direction: -1 | 1) => {
        if (index + direction < 0 || index + direction >= activeCols.length) return;
        const newCols = [...activeCols];
        const temp = newCols[index];
        newCols[index] = newCols[index + direction];
        newCols[index + direction] = temp;
        onChange('columns', newCols);
    };

    const handleSelectAll = () => {
        const newCols = activeCols.map((col: any) => ({ ...col, keep: true }));
        onChange('columns', newCols);
    };

    const handleDeselectAll = () => {
        const newCols = activeCols.map((col: any) => ({ ...col, keep: false }));
        onChange('columns', newCols);
    };

    const allSelected = activeCols.length > 0 && activeCols.every((col: any) => col.keep !== false);
    const noneSelected = activeCols.length > 0 && activeCols.every((col: any) => col.keep === false);

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-xs text-blue-800 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <Columns className="h-4 w-4 shrink-0" />
                    <strong>Columns</strong>
                </div>
            </div>

            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-700">RowID:</label>
                    <div
                        className={`w-8 h-4 rounded-full p-0.5 cursor-pointer transition-colors ${rowId ? 'bg-blue-600' : 'bg-gray-300'}`}
                        onClick={() => toggleRowId(!rowId)}
                    >
                        <div className={`w-3 h-3 bg-white rounded-full shadow-sm transform transition-transform ${rowId ? 'translate-x-4' : 'translate-x-0'}`} />
                    </div>
                    <span className="text-[10px] text-gray-500">{rowId ? 'ON' : 'OFF'}</span>
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={handleAutoDetect}
                    disabled={inputColumns.length === 0}
                    title={inputColumns.length === 0 ? "No input columns detected from upstream nodes." : "Reset all columns from input"}
                >
                    Auto Detect
                </Button>
            </div>

            {activeCols.length > 0 && (
                <div className="flex items-center gap-1">
                    <span className="text-[10px] text-gray-400 mr-1">
                        {activeCols.filter((c: any) => c.keep !== false).length} / {activeCols.length} visible
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] flex-1"
                        onClick={handleSelectAll}
                        disabled={allSelected}
                        title="Show all columns"
                    >
                        Select All
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] flex-1"
                        onClick={handleDeselectAll}
                        disabled={noneSelected}
                        title="Hide all columns"
                    >
                        Deselect All
                    </Button>
                </div>
            )}

            {inputColumns.length === 0 && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-[10px] text-yellow-700 mb-2">
                    Warning: No input columns detected. ensure the source node is configured and connected.
                </div>
            )}

            <div className="border rounded-md bg-white">
                <div className="grid grid-cols-12 bg-gray-50 p-2 text-[10px] font-bold text-gray-500 border-b">
                    <div className="col-span-1 text-center">Vis</div>
                    <div className="col-span-11 pl-2">Column</div>
                </div>

                <div className="max-h-[300px] overflow-y-auto">
                    {activeCols.map((col: any, idx: number) => (
                        <div key={idx} className={`grid grid-cols-12 items-center p-2 border-b last:border-0 hover:bg-gray-50 group ${!col.keep ? 'opacity-50' : ''}`}>
                            {/* Toggle Visibility */}
                            <div className="col-span-1 flex justify-center">
                                <button onClick={() => toggleKeep(idx)} className="text-gray-400 hover:text-purple-600 focus:outline-none">
                                    {col.keep ? <Eye className="h-4 w-4 text-purple-600" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                            </div>

                            {/* Configuration */}
                            <div className="col-span-11 pl-2 flex flex-col gap-1">
                                {/* Top Row: Original Name + Type + Reorder */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <span className="text-xs font-semibold text-gray-700 truncate" title={col.name}>{col.name}</span>
                                        <span className="text-[9px] text-gray-400 font-mono px-1 bg-gray-100 rounded">{col.type}</span>
                                    </div>

                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => moveCol(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30">
                                            <ArrowUp className="h-3 w-3 text-gray-600" />
                                        </button>
                                        <button onClick={() => moveCol(idx, 1)} disabled={idx === activeCols.length - 1} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30">
                                            <ArrowDown className="h-3 w-3 text-gray-600" />
                                        </button>
                                    </div>
                                </div>

                                {/* Bottom Row: Rename Input */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-400">As:</span>
                                    <Input
                                        className="h-6 text-xs py-0 px-1 border-gray-200 focus-visible:ring-1 focus-visible:ring-offset-0"
                                        value={col.newName || col.name}
                                        onChange={(e) => updateRename(idx, e.target.value)}
                                        placeholder="New Name"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {activeCols.length === 0 && (
                        <div className="p-4 text-center text-xs text-gray-400">
                            No columns found. Connect a source node.
                        </div>
                    )}
                </div>
            </div>

            <div className="text-[10px] text-gray-400">
                Tip: Drag handles to reorder are not yet supported, use the arrows.
            </div>
        </div>
    );
};
